import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { AlertCircle, Inbox, Plus } from "lucide-react";
import { toast } from "sonner";

import { PaginationBar } from "@/components/pagination-bar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { TransactionCard } from "@/components/waste/transaction-card";
import { FilterPopover } from "@/components/waste/filter-popover";
import { WasteEventModals } from "@/components/waste/waste-event-modals";

import { useTranslation } from "@/i18n/use-translation";
import { useWasteFilters } from "@/hooks/use-waste-filters";
import { useSearchParamWriter } from "@/hooks/use-search-param-writer";
import { useWasteEvents, useWasteEvent, type WasteEvent } from "@/hooks/use-waste-events";
import { useWasteEventActionsController } from "@/hooks/use-waste-event-actions-controller";
import { useEnabledClassificationSites } from "@/hooks/use-enabled-classification-sites";
import { useSitesWithActiveMenus, useKitchens } from "@/hooks/use-active-menus";
import { useDevices } from "@/hooks/use-devices";
import { useWasteFoods } from "@/hooks/use-waste-foods";
import { foodLabel } from "@/lib/food-labels";
import { useResolvedConfig, type EntityType } from "@/hooks/use-resolved-config";
import { useClaims } from "@/hooks/use-claims";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useAuthStore } from "@/stores/auth";

type SortOption = {
  value: string;
  /** A literal catalogue key, resolved at render — this list is module-level, where a `t()` call
   * would freeze the locale at import (POS2-1134 CP3a). */
  labelKey: string;
  sort_by: "logged_at" | "item_weight" | "cost_estimate" | "classification_level";
  sort_dir: "asc" | "desc";
};

const EMPTY_SITE_SET: ReadonlySet<string> = new Set();

const SORT_OPTIONS: SortOption[] = [
  // Default (POS2-759): highest classification level first (L4 → L1).
  {
    value: "recommended",
    labelKey: "waste.sortRecommended",
    sort_by: "classification_level",
    sort_dir: "desc",
  },
  { value: "newest", labelKey: "waste.sortNewest", sort_by: "logged_at", sort_dir: "desc" },
  { value: "heaviest", labelKey: "waste.sortHeaviest", sort_by: "item_weight", sort_dir: "desc" },
  {
    value: "highest_cost",
    labelKey: "waste.sortHighestCost",
    sort_by: "cost_estimate",
    sort_dir: "desc",
  },
];

export default function WasteTransactionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { filters, dateRange, defaultRange, hasActiveFilters, setFilter, setSort, clear } =
    useWasteFilters();

  const eventsQuery = useWasteEvents(filters, dateRange);
  const sitesQuery = useSitesWithActiveMenus();
  const kitchensQuery = useKitchens(filters.site_id);
  const devicesQuery = useDevices();
  // All kitchens (org-wide) → annotate each device with its site_id so the filter popover
  // scopes the sensor list to the chosen site, like Explore (POS2-851).
  const allKitchensQuery = useKitchens(null);
  // Defer the org-wide foods scan until the filter popover is first opened (POS2-960).
  const [filtersOpened, setFiltersOpened] = useState(false);
  const foodsQuery = useWasteFoods(filtersOpened);
  const actions = useWasteEventActionsController();
  // Stable useState setter — safe to depend on in the deep-link effect below.
  const { setCommentsEvent } = actions;
  const enabledSitesQuery = useEnabledClassificationSites();

  // Currency for the Price filter follows the org→site→kitchen config stack,
  // scoped to the active filter (same resolution as Reports / per-event cost).
  const { orgId: claimsOrgId, isPlatformAdmin, role } = useClaims();
  const adminOrgId = useAdminOrgStore((s) => s.orgId);
  const orgId = isPlatformAdmin ? adminOrgId : claimsOrgId;

  // Comment moderation mirrors the backend `is_org_wide`: org admins + platform
  // admins may delete anyone's comment; everyone else only their own (POS2-760).
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const isModerator = isPlatformAdmin || role === "Org Admin";
  const configEntity: { type: EntityType; id: string | null } = filters.kitchen_id
    ? { type: "kitchen", id: filters.kitchen_id }
    : filters.site_id
      ? { type: "site", id: filters.site_id }
      : { type: "organisation", id: orgId || null };
  const resolvedCurrency = useResolvedConfig(configEntity.type, configEntity.id).data?.config
    .currency?.value;
  const currency = typeof resolvedCurrency === "string" ? resolvedCurrency : "GBP";

  const feedRef = useRef<HTMLDivElement>(null);

  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);
  // Multi-site page: show the site name only when a site filter is active (POS2-851).
  const siteName = filters.site_id
    ? (sites.find((s) => s.id === filters.site_id)?.name ?? null)
    : null;
  const kitchens = kitchensQuery.data ?? [];
  const devices = useMemo(() => {
    const kitchenSite = new Map((allKitchensQuery.data ?? []).map((k) => [k.id, k.site_id]));
    return (devicesQuery.data ?? []).map((d) => ({
      ...d,
      site_id: kitchenSite.get(d.kitchen_id) ?? null,
    }));
  }, [devicesQuery.data, allKitchensQuery.data]);
  // The Food filter's options are inventory item names, filtered by id — so the label is
  // display-only and goes through `foodLabel` like every other food name (POS2-1134).
  const foods = useMemo(
    () => (foodsQuery.data ?? []).map((f) => ({ value: f.id, label: foodLabel(t, f.label) })),
    [foodsQuery.data, t],
  );
  const events = eventsQuery.data?.events ?? [];
  const total = eventsQuery.data?.total ?? 0;

  // Deep-link from a notification: ?commentEvent=<id>&comment=<cid> opens the
  // thread for that event (fetching it if it isn't on the current page) and
  // scrolls to the mentioned comment (POS2-761).
  const [searchParams] = useSearchParams();
  const { write: writeParams } = useSearchParamWriter({ replace: true });
  const deepLinkEventId = searchParams.get("commentEvent");
  const deepLinkCommentId = searchParams.get("comment");
  const listEvent = deepLinkEventId ? (events.find((e) => e.id === deepLinkEventId) ?? null) : null;
  const fetchedEvent = useWasteEvent(deepLinkEventId && !listEvent ? deepLinkEventId : null);
  const deepLinkEvent = listEvent ?? fetchedEvent.data ?? null;

  // Open the deep-linked thread once (POS2-761 close race): keying off
  // `commentsEvent` reopened the panel the instant the user closed it, because
  // the URL param clears a render later than `commentsEvent` nulls. A ref of the
  // last-opened id dedupes, and resets when the deep-link is gone.
  const openedDeepLinkRef = useRef<string | null>(null);
  useEffect(() => {
    const { open, openedId } = deepLinkOpenTarget(
      deepLinkEventId,
      deepLinkEvent,
      openedDeepLinkRef.current,
    );
    openedDeepLinkRef.current = openedId;
    if (open) setCommentsEvent(open);
  }, [deepLinkEventId, deepLinkEvent, setCommentsEvent]);

  // Deep-linked to an event we can't load (deleted / out of scope): don't leave
  // the user with a silently-empty panel — tell them and clear the params (POS2-847).
  useEffect(() => {
    if (deepLinkEventId && !listEvent && fetchedEvent.isError) {
      toast.error(t("waste.eventUnavailableToast"));
      writeParams((next) => {
        next.delete("commentEvent");
        next.delete("comment");
      });
    }
  }, [deepLinkEventId, listEvent, fetchedEvent.isError, writeParams, t]);

  function closeComments() {
    setCommentsEvent(null);
    if (!deepLinkEventId) return;
    // Both deletions in one update — one logical change, one history entry.
    writeParams((next) => {
      next.delete("commentEvent");
      next.delete("comment");
    });
  }

  const sitesById = useMemo(() => new Map(sites.map((s) => [s.id, s])), [sites]);
  const autoClassifyingSites = enabledSitesQuery.data ?? EMPTY_SITE_SET;

  const selectedSite = filters.site_id ? (sitesById.get(filters.site_id) ?? null) : null;
  const showMenuBanner = computeMenuBanner(selectedSite, sites);

  const currentSort =
    SORT_OPTIONS.find((o) => o.sort_by === filters.sort_by && o.sort_dir === filters.sort_dir) ??
    SORT_OPTIONS[0];

  function handleSortChange(value: string) {
    const opt = SORT_OPTIONS.find((o) => o.value === value);
    if (!opt) return;
    setSort(opt.sort_by, opt.sort_dir);
  }

  // Paging keeps the user at the top of the feed instead of mid-scroll (POS2-759).
  function handlePageChange(nextPage: number) {
    setFilter("page", nextPage);
    scrollToTop(feedRef.current);
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div>
          <h1 className="flex min-w-0 items-baseline gap-1.5 font-display text-xl font-extrabold tracking-tight text-gray-900">
            <span className="shrink-0">{t("waste.transactionsTitle")}</span>
            {siteName && (
              <>
                <span className="shrink-0 text-gray-300">·</span>
                <span className="max-w-[16rem] truncate" title={siteName}>
                  {siteName}
                </span>
              </>
            )}
          </h1>
          <p className="mt-0.5 text-xs text-gray-500">
            {eventsQuery.isLoading ? t("common.loading") : t("waste.eventCount", { count: total })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => {
              actions.setEditEvent(null);
              actions.setLogOpen(true);
            }}
          >
            <Plus className="size-3.5" aria-hidden />
            {t("waste.logWasteButton")}
          </Button>
          <Select value={currentSort.value} onValueChange={handleSortChange}>
            <SelectTrigger className="h-8 w-[180px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FilterPopover
            sites={sites}
            kitchens={kitchens}
            devices={devices}
            foods={foods}
            currency={currency}
            datesDefault={defaultRange}
            onOpenChange={(open) => open && setFiltersOpened(true)}
          />
        </div>
      </div>

      {/* Menu-context banner */}
      {showMenuBanner && (
        <div className="mx-6 mt-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
          <div className="flex-1 text-sm text-amber-900">
            {t("waste.noSiteMenu")}
            {t("waste.noSiteMenuSuffix")}
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="h-7 text-xs"
            onClick={() => navigate("/menus")}
          >
            {t("waste.uploadMenu")}
          </Button>
        </div>
      )}

      {/* Feed */}
      <div ref={feedRef} className="flex-1 overflow-y-auto p-6">
        {eventsQuery.isError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {t("waste.loadEventsFailed")} {String(eventsQuery.error)}
          </div>
        )}

        {eventsQuery.isLoading && <SkeletonGrid />}

        {!eventsQuery.isLoading && events.length === 0 && (
          <EmptyState hasFilters={hasActiveFilters} onClear={clear} />
        )}

        {!eventsQuery.isLoading && events.length > 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {events.map((event) => {
              const site = sitesById.get(event.site_id);
              const siteHasActiveMenu = site?.has_active_menu ?? false;
              return (
                <TransactionCard
                  key={event.id}
                  event={event}
                  onAnalyse={actions.onAnalyse}
                  onConfirm={actions.onConfirm}
                  onRelabel={actions.setRelabelEvent}
                  isConfirming={actions.isConfirming(event.id)}
                  isAnalysing={actions.isAnalysing(event.id)}
                  siteAutoClassifying={autoClassifyingSites.has(event.site_id)}
                  onExpandImage={actions.setLightboxEvent}
                  siteHasActiveMenu={siteHasActiveMenu}
                  onEdit={actions.setEditEvent}
                  onOpenComments={actions.setCommentsEvent}
                />
              );
            })}
          </div>
        )}
      </div>

      <PaginationBar
        page={filters.page}
        pageSize={filters.page_size}
        total={total}
        onPageChange={handlePageChange}
        onPageSizeChange={(s) => setFilter("page_size", s)}
        pageSizeOptions={[9, 12, 15, 18]}
        sticky
      />

      <WasteEventModals
        controller={actions}
        sites={sites}
        defaultSiteId={filters.site_id ?? null}
        defaultKitchenId={filters.kitchen_id ?? null}
        currentUserId={currentUserId}
        isModerator={isModerator}
        scrollToCommentId={deepLinkCommentId}
        onCloseComments={closeComments}
        embedCommentsCard
        autoClassifyingSites={autoClassifyingSites}
      />
    </div>
  );
}

/** Scrolls the feed container back to the top (POS2-759 — on page change). */
export function scrollToTop(el: Pick<HTMLElement, "scrollTo"> | null): void {
  el?.scrollTo({ top: 0 });
}

export function computeMenuBanner(
  selectedSite: { has_active_menu: boolean } | null,
  sites: { has_active_menu: boolean }[],
): boolean {
  if (selectedSite) return !selectedSite.has_active_menu;
  if (sites.length === 0) return false;
  return sites.every((s) => !s.has_active_menu);
}

/**
 * Deep-link open decision (POS2-761 close race). Open a deep-linked event's
 * thread at most once so that closing the panel (which nulls `commentsEvent`
 * before the URL param clears) isn't immediately undone by this effect
 * re-opening it. `openedId` is the "already handled" memory; it resets to null
 * once the deep-link is gone, so re-clicking the same notification reopens.
 * Returns the event to open (or null for no-op) plus the memory to store.
 */
export function deepLinkOpenTarget(
  deepLinkEventId: string | null,
  deepLinkEvent: WasteEvent | null,
  openedId: string | null,
): { open: WasteEvent | null; openedId: string | null } {
  if (!deepLinkEventId) return { open: null, openedId: null };
  if (deepLinkEvent && openedId !== deepLinkEvent.id) {
    return { open: deepLinkEvent, openedId: deepLinkEvent.id };
  }
  return { open: null, openedId };
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col gap-3 rounded-lg border border-black/[0.06] bg-white p-4"
        >
          <Skeleton className="aspect-[4/3] w-full" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/3" />
          <div className="grid grid-cols-2 gap-2">
            <Skeleton className="h-3" />
            <Skeleton className="h-3" />
            <Skeleton className="h-3" />
            <Skeleton className="h-3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ hasFilters, onClear }: { hasFilters: boolean; onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Inbox className="size-10 text-gray-300" aria-hidden />
      <h3 className="mt-3 text-sm font-medium text-gray-900">
        {hasFilters ? t("waste.emptyFiltered") : t("waste.emptyNoEvents")}
      </h3>
      <p className="mt-1 max-w-sm text-sm text-gray-500">
        {hasFilters ? t("waste.emptyFilteredHint") : t("waste.emptyNoEventsHint")}
      </p>
      {hasFilters && (
        <Button variant="secondary" size="sm" className="mt-4" onClick={onClear}>
          {t("waste.clearFilters")}
        </Button>
      )}
    </div>
  );
}
